"use client"

import { Mail, Phone, MapPin, Send, MessageSquare, Clock } from "lucide-react"
import { Button } from "@/components/ui/button"
import { motion, useInView } from "framer-motion"
import { useRef, useState, useEffect } from "react"
import { FollowerPointerCard } from "@/components/ui/following-pointer"
import { Menu } from "lucide-react"
import { useAuth } from "../contexts/AuthContext"
import {StickyFooter} from "../components/sticky-footer"
import { NewReleasePromo} from "../components/new-release-promo"
import axios from 'axios';
import { toast } from 'sonner';

const Contact = () => {
  const ref = useRef(null)
  const [isFormHovering, setIsFormHovering] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const amplyLogo = new URL('../assets/amply.png', import.meta.url).href
  const [isScrolled, setIsScrolled] = useState(false);
  const user = useAuth();

    useEffect(() => {
      const handleScroll = () => {
        setIsScrolled(window.scrollY > 100)
      }
  
      window.addEventListener("scroll", handleScroll)
      return () => window.removeEventListener("scroll", handleScroll)
    }, [])

    const handleSubmit = async (e) => {
      e.preventDefault();
  
      const name = e.target.name.value;
      const email = e.target.email.value;
      const artistName = e.target.artistName.value;
      const message = e.target.message.value;
      const turnstileToken = e.target['cf-turnstile-response'].value;
  
      if (!name || !email || !message) {
        alert('Please fill in all required fields.');
        return;
      }
  
      if (!turnstileToken) {
        alert('Please complete the CAPTCHA.');
        return;
      }
  
      try {
  const response = await axios.post('https://backend.amply.buildfast.us/send-email', {
          email,
          subject: `Contact Form Submission from ${name}`,
          message: `Name: ${name}\nArtist/Band Name: ${artistName}\nMessage: ${message}`,
          turnstileToken,
        });
  
        if (response.data.success) {
          toast.success('Message sent successfully!');
          e.target.reset();
        } else {
          toast.error('Failed to send message. Please try again later.');
        }
      } catch (error) {
        console.error('Error sending message:', error);
        toast.error('An error occurred. Please try again later.');
      }
    };

    useEffect(() => {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      document.body.appendChild(script);
      return () => {
        document.body.removeChild(script);
      };
    }, [])

  return (
    <div className="min-h-screen bg-black text-white">
       {/* Desktop Header */}
      <header
        className={`sticky top-4 z-[9999] mx-auto hidden w-full flex-row items-center justify-between self-start rounded-full bg-transparent md:flex backdrop-blur-sm border border-[#ebcebc] shadow-lg transition-all duration-300 ${
          isScrolled ? "max-w-3xl px-2" : "max-w-5xl px-4"
        } py-2`}
        style={{
          willChange: "transform",
          transform: "translateZ(0)",
          backfaceVisibility: "hidden",
          perspective: "1000px",
        }}
      >
        <a
          className={`z-50 flex items-center justify-center gap-2 transition-all duration-300 ${
            isScrolled ? "ml-4" : ""
          }`}
          href="/"
        >
                <img 
                src={amplyLogo}
                className="w-8 h-8"
                alt="Amply Logo"
                />
        </a>

  <div className="absolute inset-0 hidden flex-1 flex-row items-center justify-center space-x-2 text-sm font-medium text-white transition duration-200 hover:text-white md:flex md:space-x-2">
          <a
            className="relative px-4 py-2 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            href="./#features"
          >
            <span style={{fontWeight: 'bold'}} className="relative z-20">Features</span>
          </a>
          <a
            className="relative px-4 py-2 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            href="/about"
          >
            <span style={{fontWeight: 'bold'}} className="relative z-20">About</span>
          </a>
          <a
            className="relative px-4 py-2 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            href="/pricing"
          >
            <span style={{fontWeight: 'bold'}} className="relative z-20">Pricing</span>
          </a>
                  <a
            className="relative px-4 py-2 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            href="/contact"
          >
            <span style={{fontWeight: 'bold'}} className="relative z-20">Contact Us</span>
          </a>
        </div>

        <div className="flex items-center gap-4">
          {user ? (
            <a
              href="/dashboard"
              className="rounded-full font-bold relative cursor-pointer transition duration-300 inline-block text-center bg-[#e3c5b3] text-white shadow-lg hover:shadow-xl hover:scale-105 px-6 py-3 text-base"
            >
              Dashboard
            </a>
          ) : (
            <>
              <a
                href="/login"
                className="rounded-full border border-[#e3c5b3] font-bold relative cursor-pointer transition duration-200 inline-block text-center text-primary-foreground px-4 py-2 text-sm"
              >
                Log In
              </a>
              <a
                href="/register"
                className="rounded-full font-bold relative cursor-pointer transition duration-300 inline-block text-center bg-[#e3c5b3] text-white shadow-lg hover:shadow-xl hover:scale-105 px-6 py-3 text-base"
              >
                Sign Up
              </a>
            </>
          )}
        </div>
      </header>

      {/* Mobile Header */}
  <header className="sticky top-4 z-[9999] mx-4 flex w-auto flex-row items-center justify-between rounded-full bg-transparent backdrop-blur-sm border border-[#ebcebc] shadow-lg md:hidden px-4 py-3">
        <a
          className="flex items-center justify-center gap-2"
          href="./"
        >
                <img 
                src={amplyLogo}
                className="w-8 h-8"
                alt="Amply Logo"
                />
        </a>

        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="flex items-center justify-center w-10 h-10 rounded-full bg-background/50 border border-border/50 transition-colors hover:bg-background/80"
          aria-label="Toggle menu"
        >
          <div className="flex flex-col items-center justify-center w-5 h-5 space-y-1">
            {isMobileMenuOpen ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5 text-foreground"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
             <Menu className="w-5 h-5 text-foreground" />
            )}
          </div>
        </button>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
  <div className="fixed inset-0 z-[9998] bg-transparent backdrop-blur-sm md:hidden">
    <div className="absolute top-28 left-4 right-4 bg-transparent/80 backdrop-blur-md border border-border/50 rounded-2xl shadow-2xl p-6">
      <nav className="flex flex-col space-y-4">
        <a
            href="./#features"
          className="text-left px-4 py-3 text-lg font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-background/50"
        >
          Features
        </a>
        <a
          href="/about"
          className="text-left px-4 py-3 text-lg font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-background/50"
        >
          About
        </a>
        <a
          href="/pricing"
          className="text-left px-4 py-3 text-lg font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-background/50"
        >
          Pricing
        </a>
               <a
          href="/contact"
          className="text-left px-4 py-3 text-lg font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-background/50"
        >
          Contact Us
        </a>
        <div className="border-t border-border/50 pt-4 mt-4 flex flex-col space-y-3">
          {user ? (
            <a
              href="/dashboard"
              className="px-4 py-3 text-lg font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-background/50 cursor-pointer"
            >
              Dashboard
            </a>
          ) : (
            <>
              <a
                href="/login"
                className="px-4 py-3 text-lg font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-background/50 cursor-pointer"
              >
                Log In
              </a>
              <a
                href="/register"
                className="px-4 py-3 text-lg font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-background/50 cursor-pointer"
              >
                Sign Up
              </a>
            </>
          )}
        </div>
      </nav>
    </div>
  </div>
)}
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-black">
        <div className="absolute inset-0 bg-[url('/abstract-music-waves-pattern.jpg')] opacity-5"></div>
        <div className="pointer-events-none absolute left-1/2 top-0 z-0 h-80 w-[60vw] -translate-x-1/2 bg-gradient-radial from-[#e3c5b3]/30 via-[#e3c5b3]/10 to-transparent opacity-60 blur-2xl"></div>
        <div className="absolute top-0 left-1/2 w-full max-w-4xl -translate-x-1/2 h-[2px]">
          <div
            className="w-full h-full bg-gradient-to-r from-transparent via-[#e3c5b3] to-transparent animate-pulse"
            style={{ opacity: 0.5 }}
          />
        </div>
      <br/>
      <br/>
      <br/>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              className="flex justify-center mb-8"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              
              <div className="relative">
                <div className="absolute inset-0 bg-[#e3c5b3]/20 rounded-full blur-xl"></div>
                <div className="relative bg-[#e3c5b3]/10 p-6 rounded-full">
                  <MessageSquare className="h-16 w-16 text-[#e3c5b3]" />
                </div>
              </div>
            </motion.div>
            <motion.h1
              className="text-4xl md:text-6xl lg:text-7xl font-bold text-balance mb-6 text-white"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              style={{
                textShadow: "0 0 16px #e3c5b3, 0 0 4px white",
              }}
            >
              Contact <span className="text-[#e3c5b3]">Amply</span>
            </motion.h1>
            <motion.p
              className="text-xl md:text-2xl text-white/70 text-balance max-w-3xl mx-auto mb-8"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Ready to amplify your fan connections? Let's start the conversation about growing your music career
            </motion.p>
                      <a
                        href="mailto:support@amply.com"
                        className="inline-flex items-center gap-2 px-[50px] py-[10px] rounded-full bg-black/80 border border-[#e3c5b3]/30 backdrop-blur-sm mb-6 cursor-pointer"
                        style={{ textDecoration: 'none' }}
                      >
                        <span className="text-sm font-medium text-white">Email Us</span>
                      </a>
          </motion.div>
        </div>
      </section>
      <br/>
      <br/>
      <br/>

    
      {/* Contact Form Section */}
      <section className="text-white relative overflow-hidden py-12 sm:py-24 md:py-32 bg-black/50">
        <div className="pointer-events-none absolute left-1/2 top-0 z-0 h-80 w-[60vw] -translate-x-1/2 bg-gradient-radial from-[#e3c5b3]/30 via-[#e3c5b3]/10 to-transparent opacity-60 blur-2xl"></div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="container mx-auto flex flex-col items-center gap-6 sm:gap-12"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-black/80 border border-[#e3c5b3]/30 backdrop-blur-sm mb-6"
          >
            <MessageSquare className="w-4 h-4 text-[#e3c5b3]" />
            <span className="text-sm font-medium text-white">Send Message</span>
          </motion.div>

          <h2 className="mb-5 text-center text-4xl font-semibold tracking-tighter md:text-[54px] md:leading-[60px] relative z-10 text-white">
            Start Your Journey
          </h2>

          <FollowerPointerCard
            title={
              <div className="flex items-center gap-2">
                <span>✉️</span>
                <span>Contact Form</span>
              </div>
            }
          >
            <div className="cursor-none">
              <motion.div
                className="group border-[#e3c5b3]/40 text-white relative col-span-12 flex flex-col overflow-hidden rounded-xl border-2 p-8 shadow-xl transition-all ease-in-out max-w-4xl mx-auto bg-black/50"
                onMouseEnter={() => setIsFormHovering(true)}
                onMouseLeave={() => setIsFormHovering(false)}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                whileHover={{
                  scale: 1.01,
                  borderColor: "#e3c5b3",
                  boxShadow: "0 0 30px rgba(227, 197, 179, 0.2)",
                }}
              >
                <div className="grid lg:grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-2xl leading-none font-semibold tracking-tight mb-4 text-white">
                      Tell Us About Your Music
                    </h3>
                    <p className="text-white/70 mb-6">
                      Whether you're a solo artist, band, or music industry professional, we'd love to hear about your
                      goals and how Amply can help you build stronger fan connections.
                    </p>

                    <div className="space-y-4">
                      <form className="space-y-4" onSubmit={handleSubmit} >
                      <div>
                        <label className="block text-sm font-medium text-white mb-2">Name *</label>
                        <input
                          type="text"
                          name="name"
                          className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-[#e3c5b3] transition-colors"
                          placeholder="Your full name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-white mb-2">Email *</label>
                        <input
                          type="email"
                          name="email"
                          className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-[#e3c5b3] transition-colors"
                          placeholder="your@email.com"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-white mb-2">Artist/Band Name</label>
                        <input
                          type="text"
                          name="artistName"
                          className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-[#e3c5b3] transition-colors"
                          placeholder="Your stage name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-white mb-2">Message *</label>
                        <textarea
                          name="message"
                          rows={4}
                          className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-[#e3c5b3] transition-colors resize-none"
                          placeholder="Tell us about your music, current fan engagement challenges, and what you hope to achieve with Amply..."
                        />
                      </div>
                      
                      <div className="cf-turnstile" data-sitekey="0x4AAAAAAB1rH5w85T9GXdmo"></div>
                      <Button type="submit" className="w-full mt-6 bg-[#e3c5b3] hover:bg-[#e3c5b3]/90 text-black font-semibold">
                        Send Message
                      </Button>
                    </form>
                  </div>

                  </div>

                  <div className="flex grow items-center justify-center select-none relative min-h-[400px]">
                    <div className="relative w-full h-[400px] rounded-xl overflow-hidden bg-gradient-to-br from-[#e3c5b3]/10 to-[#e3c5b3]/5">
                      {/* Form Interaction Visualization */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="relative">
                          {/* Central Message Icon */}
                          <motion.div
                            className="w-20 h-20 bg-[#e3c5b3] rounded-full flex items-center justify-center text-black font-bold shadow-lg relative z-10"
                            animate={isFormHovering ? { scale: [1, 1.1, 1] } : { scale: 1 }}
                            transition={{ duration: 1, repeat: isFormHovering ? Number.POSITIVE_INFINITY : 0 }}
                          >
                            <Send className="w-10 h-10" />
                          </motion.div>

                        
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </FollowerPointerCard>
        </motion.div>
      </section>

     {/* Pricing Section */}
      <div id="cta">
      <NewReleasePromo />
      </div>

      <div className="h-[500px]"/>
      <StickyFooter />
    </div>
  )
}

export default Contact
